// Copyright 2019 Sorama B.V.

import { BindingEngine, Disposable, TaskQueue, autoinject, computedFrom, transient } from "aurelia-framework";
import {
    IObservable,
    NavigationCommand,
    NavigationInstruction,
    Redirect,
    RoutableComponentActivate,
    RoutableComponentCanActivate,
    RoutableComponentDeactivate,
    RouteConfig,
    Router,
} from "aurelia-router";
import { IWorkflowModuleCatalog } from "catalogs/IWorkflowModuleCatalog";
import { IWorkflowSettings } from "libraries/WorkflowFramework/Interfaces/IWorkflowSettings";
import { TimelineEntriesRepository } from "repositories/TimelineEntriesRepository";
import { ModuleRestoreStep } from "services/Module/ModuleRestoreStep";
import { WorkflowModeService } from "services/WorkflowModes/WorkflowModeService";
import { Sortable } from "sortablejs";
import { IUrlStateService } from "../Interfaces/IUrlStateService";
import { IWorkflowModuleConfig } from "../Interfaces/IWorkflowModuleConfig";
import { IWorkflowModuleController, IWorkflowRouter } from "../Interfaces/IWorkflowRouter";
import { WorkflowProperty, WorkflowPropertyManager } from "../WorkflowProperties/WorkflowPropertyObserverManager";
import {
    WorkflowPropertyUriConverter,
    WorkflowUriModule,
    WorkflowUriProperty,
} from "../WorkflowProperties/WorkflowPropertyUriConverter";
import { WorkflowModuleController } from "./WorkflowModuleController";

/**
 * Entry point for navigation in a workflow. Aurelia will start a navigation action to this class. This class is responsible
 * for correct navigation to the WorkflowModules.
 */
@transient()
@autoinject
export class WorkflowRouter
    implements IWorkflowRouter, RoutableComponentCanActivate, RoutableComponentActivate, RoutableComponentDeactivate
{
    @computedFrom("_workflowSettings.workflowName")
    public get workflowName(): string {
        return this.workflowSettings ? this.workflowSettings.workflowName : undefined;
    }

    public get workflowProperties(): WorkflowProperty[] {
        return this.workflowPropertyManager ? this.workflowPropertyManager.workflowProperties : undefined;
    }

    public workflowModules: WorkflowModuleController[] = []; // Array holding the current workflow modules

    private workflowSettings: IWorkflowSettings; // Workflow settings retrieved from navigation
    private subscribers: (() => void)[] = [];
    private disposables: Disposable[] = [];

    private currentModuleIdCandidate = 0;
    private initialWorkflowModulesAdded = false;

    public static readonly Prefix = ""; // yeah, empty prefix is correct ;)
    public static readonly TimelinePrefix = "timeline";

    constructor(
        private readonly workflowPropertyManager: WorkflowPropertyManager,
        private readonly moduleCatalog: IWorkflowModuleCatalog,
        private readonly bindingEngine: BindingEngine,
        private readonly taskQueue: TaskQueue,
        private readonly router: Router,
        private readonly urlStateService: IUrlStateService,
        private readonly moduleRestoreStep: ModuleRestoreStep,
        private readonly timelineEntryRepo: TimelineEntriesRepository = undefined,
        private readonly workflowModeService: WorkflowModeService
    ) {}

    public canActivate(
        params: unknown,
        routeConfig: RouteConfig,
        navigationInstruction: NavigationInstruction
    ):
        | boolean
        | Promise<boolean>
        | PromiseLike<boolean>
        | NavigationCommand
        | Promise<NavigationCommand>
        | PromiseLike<NavigationCommand> {
        const settings = navigationInstruction.config.settings;

        // Save copy of workflow settings
        if (!settings.workflowSettings) {
            console.log(
                "WorkflowRouter: cannot activate workflow, because workflowSettings field is missing from RouteConfig."
            );
            return new Redirect("/invalidroute");
        }

        const url: string = navigationInstruction.fragment;

        let modules = WorkflowPropertyUriConverter.workflowModulesFromUri(new URL(url, window.location.origin));

        if (modules.length == 0) {
            const startModules = Array.isArray(settings.workflowSettings.defaultStartModules)
                ? settings.workflowSettings.defaultStartModules.join("/")
                : settings.workflowSettings.defaultStartModules;
            modules = WorkflowPropertyUriConverter.workflowModulesFromUri(
                new URL(url + "/" + startModules, window.location.origin)
            );
        }

        for (const module of modules) {
            if (
                !this.moduleCatalog.getWorkflowModules().find((x) => x.name.toLowerCase() === module.name.toLowerCase())
            ) {
                return new Redirect("/invalidroute");
            }
        }

        // Note: we ignore invalid workflow properties for now. We just navigate to the correct workflow.
        return true;
    }

    /**
     * The activate method is called when the component is activated.
     * It initializes subscribers, loads workflow settings and modules,
     * and ensures that the modules are in a usable state for the user.
     *
     * @param params - Parameters passed during navigation (not used here)
     * @param routeConfig - Configuration of the current route
     * @param navigationInstruction - Instruction containing navigation details
     * @returns Promise resolving once modules are loaded and properties are updated
     */
    public activate(
        params: unknown,
        routeConfig: RouteConfig,
        navigationInstruction: NavigationInstruction
    ): void | Promise<void> | PromiseLike<void> | IObservable {
        // Initialize subscribers to workflow property changes
        this.initializeSubscriptions();

        // Load settings from the navigation instruction
        const settings = navigationInstruction.config.settings;
        this.workflowSettings = settings.workflowSettings;

        // Retrieve the current state of workflow modules from the URL state service
        let workflowModules = this.urlStateService.getNavigationState();

        // If no modules are loaded, use the default start modules defined in settings
        if (workflowModules.length === 0) {
            const startModules = Array.isArray(settings.workflowSettings.defaultStartModules)
                ? settings.workflowSettings.defaultStartModules.join("/")
                : settings.workflowSettings.defaultStartModules;
            workflowModules = WorkflowPropertyUriConverter.workflowModulesFromUri(
                new URL(navigationInstruction.fragment + "/" + startModules, window.location.origin)
            );
        }

        // Filter modules based on the current workflow mode
        workflowModules = this.modulesFilteredByWorkflowMode(workflowModules);

        // Update the modules and load workflow properties after the modules are set
        return this.updateModules(workflowModules).then(() => {
            return this.loadWorkflowProperties();
        });
    }

    /**
     * Initializes subscriptions for workflow properties and module updates.
     * This allows the component to respond to changes in workflow properties
     * and automatically update the URL when modules are added or modified.
     */
    private initializeSubscriptions(): void {
        // Subscribe to changes in workflow properties
        this.workflowPropertyManager.subscribeWorkflowPropertyChanged((properties: WorkflowUriProperty[]) => {
            this.updateWorkflowProperties(properties);
        });

        // Observe changes to the workflowModules array
        this.disposables.push(
            this.bindingEngine.collectionObserver(this.workflowModules).subscribe(() => {
                // Update the URL if initial workflow modules have been added
                if (this.initialWorkflowModulesAdded) {
                    this.updateUrl();
                }
                this.notifySubscribers();
            })
        );
    }

    /**
     * The attached method is called when the component is added to the DOM.
     * It initializes the Sortable.js functionality for draggable workflow modules.
     */
    public attached(): void {
        const draggableWorkflowModules = document.getElementById("draggable-workflow-modules");

        const handleIds = [
            "#timeanalysis",
            "#spectrumanalysis",
            "#spectrogramanalysis",
            "#farfieldanalysis",
            "#nearfieldanalysis",
            "#psychoacoustics",
        ];

        if (draggableWorkflowModules) {
            Sortable.create(draggableWorkflowModules, {
                animation: 150,
                fallbackOnBody: false,
                swapThreshold: 0.95,
                direction: "horizontal",
                handle: handleIds.join(", "), // Use the array of handles in the handle option
                ghostClass: "ghost", // Class for the ghost element during drag
                onMove: function (evt) {
                    // Prevents dropping over the item
                    if (evt.related && evt.related.id === "WorkflowModule0") {
                        return false;
                    }
                    return true; // Allow other movements
                },
            });
        }
    }

    /**
     * Loads workflow properties and updates the URL state.
     * This method is called after modules have been updated to ensure
     * that the workflow properties are in sync with the current state.
     *
     * @returns Promise that resolves once workflow properties are loaded
     */
    private loadWorkflowProperties(): Promise<void> {
        return this.updateWorkflowProperties(this.urlStateService.getPropertyState(WorkflowRouter.Prefix))
            .then(() => {
                // Initial modules have been added, update our URL
                this.initialWorkflowModulesAdded = true;
                this.updateUrl(true);
                return this.restoreTimelineEntries(); // Ensure this returns a Promise<void>
            })
            .then(() => undefined);
    }

    /**
     * Restores timeline entries from the URL state, ensuring that the
     * workflow modules reflect the user's previous interactions with the application.
     *
     * @returns Promise resolving to an array of void after restoring all entries
     */
    private restoreTimelineEntries(): Promise<void[]> {
        const timelineProps = this.urlStateService.getPropertyState(WorkflowRouter.TimelinePrefix);

        // ?[timeline]entry=id
        const entryIdProps = timelineProps.filter((x) => x.name === "entryId");

        const promises: Promise<void>[] = [];

        // Iterate over each entry ID and restore the corresponding module state
        for (const entryId of entryIdProps) {
            promises.push(
                this.timelineEntryRepo
                    .getWorkflowModuleEntry(entryId.value as string)
                    .then((entry) => {
                        const moduleConfig = entry.getWorkflowModuleConfig();

                        if (moduleConfig === undefined) {
                            // Skip if no ModuleConfig is available
                            return Promise.resolve();
                        }

                        return this.addModule(moduleConfig).then(async (module) => {
                            // Wait for activateModule to set the moduleInstance first
                            await setTimeout(() => {
                                return module.moduleInstance.restoreModuleState(entry, false);
                            }, 100);
                        });
                    })
                    .catch((e) => {
                        console.warn(e); // Handle any errors that occur during restoration
                    })
                    .finally(() => {
                        // Clean up URL states after restoration
                        // Ensure it happens also after failed restoration. Otherwise refresh creates a new instance.
                        this.urlStateService.deletePropertyStates(WorkflowRouter.TimelinePrefix);
                    })
            );
        }
        return Promise.all(promises);
    }

    public canDeactivate(navigationInstruction: NavigationInstruction): boolean {
        const module = this.workflowModules.find((m) => m.moduleInstance?.moduleReference?.canDeactivate);
        if (module) {
            const moduleReference = module.moduleInstance.moduleReference;
            return moduleReference.canDeactivate.call(moduleReference, navigationInstruction.config.route);
        }
        return true;
    }

    public deactivate(): void {
        while (this.workflowModules.length > 0) {
            this.closeModule(this.workflowModules[0].moduleId);
        }

        this.workflowPropertyManager.dispose();
        this.disposables.forEach((x) => x.dispose());
    }

    public addModule(
        workflowModule: WorkflowUriModule,
        position?: number,
        variant?: string
    ): Promise<IWorkflowModuleController> {
        // When duplicate or no Id, generate a new one.
        if (
            workflowModule.id == undefined ||
            this.workflowModules.findIndex((x) => x.moduleId == workflowModule.id) != -1
        ) {
            // Make sure that we chose a id that is not used by a module nor by a workflow property
            while (
                this.workflowModules.find((x) => x.moduleId == this.currentModuleIdCandidate) ||
                this.workflowPropertyManager.workflowProperties.findIndex(
                    (x) => x.scopeIdxs && x.scopeIdxs.findIndex((y) => y == this.currentModuleIdCandidate) > -1
                ) > -1
            ) {
                this.currentModuleIdCandidate++;
            }

            workflowModule.id = this.currentModuleIdCandidate;
            this.currentModuleIdCandidate++;
        }

        const controller = new WorkflowModuleController(
            this,
            this.moduleCatalog
                .getWorkflowModules()
                .find((y) => y.name.toLowerCase() === workflowModule.name.toLowerCase()),
            workflowModule.id,
            variant ?? workflowModule.variant
        );

        // Add to the list
        if (position) {
            this.workflowModules.splice(position, 0, controller);
        } else {
            this.workflowModules.push(controller);
        }

        // Make sure that the position is correct
        if (workflowModule.position != undefined) {
            this.moveModule(controller.moduleId, workflowModule.position);
        }

        this.exitFullScreen();

        if (this.taskQueue) {
            return new Promise((resolve, reject) => {
                this.taskQueue.queueTask(() => {
                    controller.makeActive();
                    resolve(controller);
                });
            });
        }

        return Promise.resolve(controller);
    }

    public getModule(moduleId: number): IWorkflowModuleController {
        return this.workflowModules.find((x) => x.moduleId == moduleId);
    }

    public getModules(workflowModule?: IWorkflowModuleConfig): IWorkflowModuleController[] {
        return this.workflowModules.filter((x) => !workflowModule || x.config.modulePath == workflowModule.modulePath);
    }

    public moduleTypeExists(workflowModuleType: IWorkflowModuleConfig): boolean {
        return this.workflowModules.findIndex((x) => x.config.modulePath == workflowModuleType.modulePath) != -1;
    }

    public subscribeModulesChanged(callback: () => void): Disposable {
        this.subscribers.push(callback);
        return <Disposable>{
            dispose: () => {
                const idx = this.subscribers.findIndex((x) => x == callback);
                if (idx !== -1) this.subscribers.splice(idx, 1);
            },
        };
    }

    public moveModule(workflowModuleId: number, newPosition: number): void {
        const idx = this.workflowModules.findIndex((x) => x.moduleId == workflowModuleId);

        if (idx !== -1) {
            this.workflowModules.splice(newPosition, 0, this.workflowModules.splice(idx, 1)[0]);
            this.scrollIntoView(workflowModuleId);
        }
    }

    public scrollIntoView(moduleId: number): void {
        const element = document.getElementById("WorkflowModule" + moduleId);
        if (element) {
            const bounding = element.getBoundingClientRect();
            const parent = element.parentElement;

            if (
                bounding.left >= 0 &&
                bounding.left <= parent.clientWidth &&
                bounding.right >= 0 &&
                bounding.right <= parent.clientWidth
            ) {
                // Element is already visible, so do nothing
                return;
            }

            element.scrollIntoView({ behavior: "smooth", inline: "nearest" });
        }
    }

    public closeModule(moduleId: number): void {
        const moduleIdx = this.workflowModules.findIndex((x) => x.moduleId == moduleId);

        if (moduleIdx != -1) {
            this.workflowModules.splice(moduleIdx, 1);
        }

        this.exitFullScreen();

        // The first module is closed or there are no modules left. Navigate to 'home'. Only when the router itself is not navigating.
        if (!this.router.isNavigatingNew && (moduleIdx == 0 || this.workflowModules.length == 0)) {
            this.urlStateService.deletePropertyStates(WorkflowRouter.Prefix);
            this.urlStateService.updateNavigationState("", []);
            this.urlStateService.updatePropertyState([]);
        }
    }

    public getModulePosition(moduleId: number): number {
        return this.workflowModules.findIndex((x) => x.moduleId == moduleId);
    }

    /**
     * Replace the current WorkflowModules with the new ones.
     * @param workflowModule
     */
    private updateModules(workflowModules: WorkflowUriModule[]): Promise<void> {
        // clear our list
        this.workflowModules.splice(0, this.workflowModules.length);

        const promises: Promise<unknown>[] = [];

        for (const workflowModule of workflowModules) {
            promises.push(this.addModule(workflowModule));
        }

        return Promise.all(promises).then(() => {});
    }

    private updateWorkflowProperties(uriProperties: WorkflowUriProperty[]): Promise<void> {
        this.workflowPropertyManager.updateWorkflowProperties(uriProperties);

        // No url updatesd before the initial modules have been added.
        if (this.initialWorkflowModulesAdded) {
            this.updateUrl();
        }

        this.notifySubscribers();
        return Promise.resolve();
    }

    private updateUrl(forceReplace?: boolean): void {
        const uriModules: WorkflowUriModule[] = [];
        for (let i = 0; i < this.workflowModules.length; i++) {
            uriModules.push(<WorkflowUriModule>{
                name: this.workflowModules[i].config.name,
                position: i,
                id: this.workflowModules[i].moduleId,
                variant: this.workflowModules[i].variant,
            });
        }

        let replace = forceReplace;
        if (!replace) {
            // Check if the current navigation state modules matches the new state, if so, replace the url
            const navState = this.urlStateService.getNavigationState();
            if (navState.length == uriModules.length) {
                let sameInstruction = true;
                for (const existingModule of navState) {
                    const module = uriModules.find(
                        (x) =>
                            x.id == existingModule.id &&
                            x.position == existingModule.position &&
                            x.name == existingModule.name &&
                            x.variant == existingModule.variant
                    );
                    if (!module) {
                        sameInstruction = false;
                        break;
                    }
                }

                replace = sameInstruction;
            }
        }

        this.moduleRestoreStep.registerUriForWorkflow(
            this.workflowSettings.workflowName,
            WorkflowPropertyUriConverter.workflowModulesToUri(uriModules) + window.location.search
        );
        this.urlStateService.updateNavigationState(this.workflowSettings.workflowName, uriModules, !!replace);
        this.urlStateService.deletePropertyStates(WorkflowRouter.Prefix);
        this.urlStateService.updatePropertyState(this.workflowPropertyManager.workflowProperties);
    }

    private exitFullScreen(): void {
        if (document.fullscreenElement) {
            document.exitFullscreen();
        }
    }

    private notifySubscribers(): void {
        for (const subscriber of this.subscribers) {
            try {
                subscriber();
            } catch (e) {
                console.warn(e);
            }
        }
    }

    private modulesFilteredByWorkflowMode(modules: WorkflowUriModule[]): WorkflowUriModule[] {
        return modules.filter((workflowUriModule) => {
            const moduleConfig = this.moduleCatalog
                .getWorkflowModules()
                .find((y) => y.name.toLowerCase() === workflowUriModule.name.toLowerCase());
            const disableForWorkflowModes = moduleConfig.disableForWorkflowModes;
            if (!disableForWorkflowModes) return true;
            return !disableForWorkflowModes.includes(this.workflowModeService.selectedWorkflowMode);
        });
    }
}
